import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import WhatsAppFAB from "./WhatsAppFAB";
import {
  ArrowLeft, ArrowRight, Lock, Share2, Sliders, RotateCcw, Check,
  Users, FileText, AlertCircle
} from "lucide-react";

const fmtUSD = (n, dec = 0) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  }).format(n);
const fmtNum = (n, dec = 0) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  }).format(n);

// ============== CONSTANTES ==============
const SEMANAS = 52;
const LEAD_COST = 4;          // $4 por lead
const VISA_PRICE = 49;        // $49 asesoría visa

const DEFAULTS = {
  vendedores: 1,
  leadsDia: 30,
  diasSem: 6,
  cierreCert: 5,        // %  (rango 2-10)
  precioCert: 149,      // $  (rango 99-299)
  personasFam: 2,       // personas por familia (rango 2-4)
  visaConv: 5,          // %  (rango 2-20)
};

// ============== SLIDER ==============
const Slider = ({ label, value, min, max, step = 1, onChange, suffix = "%", color = "#c9a227", marks = [], note = "" }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="text-xs font-bold text-slate-700">{label}</label>
      <span className="text-xl sm:text-2xl font-black tabular-nums" style={{ color }}>
        {value}{suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full"
      style={{ accentColor: color }}
      data-testid={`slider-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    />
    {marks.length > 0 && (
      <div className="flex justify-between text-[10px] text-slate-400 mt-1 px-0.5">
        {marks.map((m) => <span key={m.v}>{m.lbl}</span>)}
      </div>
    )}
    {note && <p className="text-[11px] text-slate-500 mt-1 italic">{note}</p>}
  </div>
);

const CertificadosSimuladorPage = () => {
  const [scrolled, setScrolled] = useState(false);

  const [vendedores, setVendedores] = useState(DEFAULTS.vendedores);
  const [leadsDia, setLeadsDia] = useState(DEFAULTS.leadsDia);
  const [diasSem, setDiasSem] = useState(DEFAULTS.diasSem);
  const [cierreCert, setCierreCert] = useState(DEFAULTS.cierreCert);
  const [precioCert, setPrecioCert] = useState(DEFAULTS.precioCert);
  const [personasFam, setPersonasFam] = useState(DEFAULTS.personasFam);
  const [visaConv, setVisaConv] = useState(DEFAULTS.visaConv);

  useEffect(() => {
    document.title = "Negocio Certificados — Travel Empire / Club Viajemos";
    const onScroll = () => setScrolled(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const reset = () => {
    setVendedores(DEFAULTS.vendedores);
    setLeadsDia(DEFAULTS.leadsDia);
    setDiasSem(DEFAULTS.diasSem);
    setCierreCert(DEFAULTS.cierreCert);
    setPrecioCert(DEFAULTS.precioCert);
    setPersonasFam(DEFAULTS.personasFam);
    setVisaConv(DEFAULTS.visaConv);
  };

  const calc = useMemo(() => {
    const leadsMes = (vendedores * leadsDia * diasSem * SEMANAS) / 12;
    const costoLeadsMes = leadsMes * LEAD_COST;

    // Certificado
    const compradoresCertMes = leadsMes * (cierreCert / 100);
    const certVendidosMes = compradoresCertMes * personasFam;
    const ingresoCertMes = certVendidosMes * precioCert;

    // Exit Sale Visa (sobre los que NO compraron certificado)
    const leadsRestantes = leadsMes - compradoresCertMes;
    const compradoresVisaMes = leadsRestantes * (visaConv / 100);
    const visasMes = compradoresVisaMes * personasFam;
    const ingresoVisaMes = visasMes * VISA_PRICE;

    // Utilidades
    const ingresoTotalMes = ingresoCertMes + ingresoVisaMes;
    const utilidadMes = ingresoTotalMes - costoLeadsMes;
    const utilidadAnual = utilidadMes * 12;
    const utilidadCertMes = ingresoCertMes - costoLeadsMes;
    const margen = ingresoTotalMes > 0 ? utilidadMes / ingresoTotalMes : 0;
    const roi = costoLeadsMes > 0 ? utilidadMes / costoLeadsMes : 0;

    return {
      leadsMes, costoLeadsMes,
      compradoresCertMes, certVendidosMes, ingresoCertMes,
      leadsRestantes, compradoresVisaMes, visasMes, ingresoVisaMes,
      ingresoTotalMes, utilidadMes, utilidadAnual, utilidadCertMes,
      margen, roi,
    };
  }, [vendedores, leadsDia, diasSem, cierreCert, precioCert, personasFam, visaConv]);

  return (
    <div data-testid="negocio-certificados-page" className="min-h-screen bg-white">
      {/* Header */}
      <header className={`border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-50 transition-shadow ${scrolled ? "shadow-md" : ""}`}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/analisisfinanciero" data-testid="back-financiero" className="flex items-center gap-1 text-slate-500 hover:text-[#1D1D1B] text-sm font-medium">
            <ArrowLeft className="h-4 w-4" /> Análisis Financiero
          </Link>
          <div className="w-px h-5 bg-slate-200" />
          <p className="text-sm font-semibold text-[#1D1D1B] truncate flex-1">Negocio Certificados</p>
          <Link
            to="/analisisfinanciero/club-vacacional"
            data-testid="link-negocio-club-vacacional"
            className="hidden md:flex items-center gap-1.5 bg-[#c9a227]/15 hover:bg-[#c9a227]/25 text-[#a17c1a] text-xs font-black px-3 py-1.5 rounded-lg transition-colors border border-[#c9a227]/30"
          >
            Club Vacacional <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to="/analisisfinanciero"
            data-testid="link-compartir"
            className="hidden sm:flex items-center gap-1.5 bg-[#1d4ed8]/15 hover:bg-[#1d4ed8]/25 text-[#1d4ed8] text-xs font-black px-3 py-1.5 rounded-lg transition-colors border border-[#1d4ed8]/30"
          >
            Compartir <ArrowRight className="h-3 w-3" />
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* HERO */}
      <section data-testid="cert-hero" className="bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <div className="inline-flex items-center gap-2 bg-[#80C753]/15 text-[#80C753] px-3 py-1 rounded-full text-[10px] font-mono tracking-[3px] uppercase mb-5">
            <FileText className="h-3 w-3" /> Otros negocios Travel Empire
          </div>
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black leading-[0.95] mb-6 max-w-4xl">
            Negocio<br/>
            <span className="text-[#80C753]">Certificados de Viaje.</span><br/>
            <span className="text-[#c9a227]" data-testid="cert-hero-anual">{fmtUSD(Math.max(0, calc.utilidadAnual))}</span><span className="text-[#c9a227]"> al año.</span>
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-5 text-sm sm:text-base">
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Certificados</span>
              <span className="font-black text-white tabular-nums">{fmtUSD(Math.max(0, (calc.ingresoCertMes - calc.costoLeadsMes) * 12))}</span>
            </div>
            <span className="text-slate-500">+</span>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Exit Sale (visas)</span>
              <span className="font-black text-[#80C753] tabular-nums">{fmtUSD(Math.max(0, calc.ingresoVisaMes * 12))}</span>
            </div>
          </div>
          <p className="text-slate-300 text-lg sm:text-xl max-w-3xl">
            Volumen alto, ticket bajo. Vendés un único paquete que incluye <strong className="text-white">5 días en Orlando + 3 en Miami + 7 días de auto + 3 días de crucero a Bahamas</strong>. El broker se queda con el 100% de la venta del certificado.
          </p>
        </div>
      </section>

      {/* SIMULADOR */}
      <section data-testid="cert-simulador" className="bg-slate-50 border-y-4 border-[#80C753]">
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
          <div className="flex items-start sm:items-center justify-between gap-3 mb-5 flex-col sm:flex-row">
            <div>
              <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#80C753] mb-1 flex items-center gap-1.5">
                <Sliders className="h-3 w-3" /> Prueba tus propios números
              </p>
              <h2 className="text-2xl sm:text-4xl font-black text-[#1D1D1B] leading-tight">
                Simulador interactivo
              </h2>
            </div>
            <button
              type="button"
              onClick={reset}
              data-testid="btn-reset-cert"
              className="flex items-center gap-1.5 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg border border-slate-200 transition-colors"
            >
              <RotateCcw className="h-3 w-3" /> Volver a valores base
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* OPERACIÓN */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-[#80C753]/40 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="w-8 h-8 rounded-lg bg-[#80C753]/15 text-[#80C753] flex items-center justify-center text-sm font-black">★</div>
                <h3 className="font-black text-[#1D1D1B]">Operación</h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#80C753]/10 text-[#80C753]">
                  Volumen sin oficina extra (gastos ya cubiertos)
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Slider
                  label="Vendedores"
                  value={vendedores} min={1} max={50} onChange={setVendedores}
                  color="#80C753" suffix=""
                  marks={[{ v: 1, lbl: "1" }, { v: 10, lbl: "10" }, { v: 25, lbl: "25" }, { v: 50, lbl: "50" }]}
                  note="Como broker puedes llegar a 50 vendedores."
                />
                <Slider
                  label="Leads por día por vendedor"
                  value={leadsDia} min={10} max={100} step={5} onChange={setLeadsDia}
                  color="#1d4ed8" suffix=""
                  marks={[{ v: 10, lbl: "10" }, { v: 30, lbl: "30 base" }, { v: 60, lbl: "60" }, { v: 100, lbl: "100" }]}
                  note="Volumen de leads diarios trabajados por cada vendedor."
                />
                <Slider
                  label="Días que trabajas por semana"
                  value={diasSem} min={1} max={7} onChange={setDiasSem}
                  color="#E04A6F" suffix=""
                  marks={[{ v: 1, lbl: "1" }, { v: 3, lbl: "3" }, { v: 6, lbl: "6 base" }, { v: 7, lbl: "7" }]}
                  note="De 1 a 7 días por semana."
                />
              </div>

              <div className="mt-4 bg-slate-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border border-slate-200">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Leads/mes</p>
                  <p className="text-xl font-black text-[#1d4ed8] tabular-nums">{fmtNum(calc.leadsMes, 0)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Costo leads/mes</p>
                  <p className="text-xl font-black text-red-600 tabular-nums">{fmtUSD(calc.costoLeadsMes)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Compradores cert.</p>
                  <p className="text-xl font-black text-[#80C753] tabular-nums">{fmtNum(calc.compradoresCertMes, 0)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Compradores visa</p>
                  <p className="text-xl font-black text-[#c9a227] tabular-nums">{fmtNum(calc.compradoresVisaMes, 0)}</p>
                </div>
              </div>
            </div>

            {/* CERTIFICADO */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#80C753]/15 text-[#80C753] flex items-center justify-center text-sm font-black">1</div>
                <h3 className="font-black text-[#1D1D1B]">Venta del certificado</h3>
              </div>

              <Slider
                label="Tasa de cierre del certificado"
                value={cierreCert} min={2} max={10} step={1} onChange={setCierreCert}
                color="#80C753"
                marks={[{ v: 2, lbl: "2% mín." }, { v: 5, lbl: "5%" }, { v: 7, lbl: "7%" }, { v: 10, lbl: "10% top" }]}
                note="% de leads que compran el paquete (mínimo 2%, máximo 10%)."
              />

              <div className="mt-4 pt-4 border-t border-slate-100">
                <Slider
                  label="Precio del certificado por persona"
                  value={precioCert} min={99} max={299} step={10} onChange={setPrecioCert}
                  color="#c9a227" suffix=""
                  marks={[{ v: 99, lbl: "$99" }, { v: 149, lbl: "$149" }, { v: 199, lbl: "$199" }, { v: 299, lbl: "$299" }]}
                  note="Paquete: 5d Orlando + 3d Miami + 7d auto + 3d crucero Bahamas."
                />
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <Slider
                  label="Personas por familia"
                  value={personasFam} min={2} max={4} step={1} onChange={setPersonasFam}
                  color="#1d4ed8" suffix=""
                  marks={[{ v: 2, lbl: "2 base" }, { v: 3, lbl: "3" }, { v: 4, lbl: "4" }]}
                  note="Cada compra incluye al titular + familiares (de 2 a 4 personas)."
                />
              </div>
            </div>

            {/* EXIT SALE */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#c9a227]/15 text-[#c9a227] flex items-center justify-center text-sm font-black">2</div>
                <h3 className="font-black text-[#1D1D1B]">Exit Sale · Asesoría de visa</h3>
              </div>

              <Slider
                label="% de leads no compradores que toman la visa"
                value={visaConv} min={2} max={20} step={1} onChange={setVisaConv}
                color="#c9a227"
                marks={[{ v: 2, lbl: "2% mín." }, { v: 5, lbl: "5%" }, { v: 12, lbl: "12%" }, { v: 20, lbl: "20% top" }]}
                note="A los leads que no compraron certificado se les vende la asesoría de visa a $49 por persona."
              />

              <div className="mt-5 bg-[#c9a227]/8 rounded-xl p-4 border border-[#c9a227]/20">
                <div className="flex items-start gap-2 text-xs text-slate-700">
                  <AlertCircle className="h-4 w-4 text-[#c9a227] flex-shrink-0 mt-0.5" />
                  <p>El precio de la visa ($49 por persona) viene fijo del modelo principal. Aquí controlas solo la conversión.</p>
                </div>
              </div>
            </div>
          </div>

          {/* RESULTADOS LIVE */}
          <div className="mt-5 bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white rounded-2xl p-6 sm:p-8 border border-[#80C753]/30">
            <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#80C753] mb-3">Resultados en vivo</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Ingreso bruto/mes</p>
                <p className="text-2xl sm:text-3xl font-black text-white tabular-nums">{fmtUSD(Math.max(0, calc.ingresoTotalMes))}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Costo marketing</p>
                <p className="text-2xl sm:text-3xl font-black text-red-400 tabular-nums">−{fmtUSD(calc.costoLeadsMes)}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Utilidad/mes</p>
                <p className="text-2xl sm:text-3xl font-black text-[#80C753] tabular-nums" data-testid="util-mes-cert">{fmtUSD(Math.max(0, calc.utilidadMes))}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Utilidad/año</p>
                <p className="text-2xl sm:text-3xl font-black text-[#c9a227] tabular-nums" data-testid="util-anual-cert">{fmtUSD(Math.max(0, calc.utilidadAnual))}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-4 text-xs sm:text-sm">
              <div>
                <span className="text-slate-400">Margen sobre ingreso: </span>
                <strong className="text-white">{(calc.margen * 100).toFixed(1)}%</strong>
              </div>
              <div>
                <span className="text-slate-400">ROI sobre marketing: </span>
                <strong className="text-[#80C753]">{calc.roi.toFixed(1)}x</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FUNNEL */}
      <section data-testid="cert-funnel" className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#80C753] mb-3">El embudo del Negocio Certificados</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-3">
            Cómo se transforma cada lead
          </h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-3xl mb-10">
            Cada lead que entra termina en una de tres rutas: compra certificado, compra visa o no compra. Las tres se calculan acá abajo en vivo.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <div className="bg-slate-50 rounded-2xl p-5 border-2 border-slate-200">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-slate-500 mb-1">Paso 1</p>
              <p className="text-xs text-slate-600 mb-3">Leads totales del mes</p>
              <p className="text-3xl sm:text-4xl font-black text-[#1D1D1B] tabular-nums">{fmtNum(calc.leadsMes, 0)}</p>
              <p className="text-xs text-slate-500 mt-2">a $4 c/u = <strong className="text-red-600">{fmtUSD(calc.costoLeadsMes)}</strong></p>
            </div>

            <div className="bg-[#80C753]/10 rounded-2xl p-5 border-2 border-[#80C753]/30">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#80C753] mb-1">Paso 2 · {cierreCert}%</p>
              <p className="text-xs text-slate-600 mb-3">Compran certificado</p>
              <p className="text-3xl sm:text-4xl font-black text-[#80C753] tabular-nums">{fmtNum(calc.compradoresCertMes, 0)}</p>
              <p className="text-xs text-slate-500 mt-2">{fmtNum(calc.certVendidosMes, 0)} certificados ({personasFam} pers./fam) × ${precioCert}</p>
            </div>

            <div className="bg-[#c9a227]/10 rounded-2xl p-5 border-2 border-[#c9a227]/30">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-1">Paso 3 · {visaConv}%</p>
              <p className="text-xs text-slate-600 mb-3">Compran visa (de los que no agendaron)</p>
              <p className="text-3xl sm:text-4xl font-black text-[#c9a227] tabular-nums">{fmtNum(calc.compradoresVisaMes, 0)}</p>
              <p className="text-xs text-slate-500 mt-2">{fmtNum(calc.visasMes, 0)} visas × $49</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white rounded-2xl p-6 sm:p-8 border border-[#80C753]/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Ingreso certificados/mes</p>
                <p className="text-2xl sm:text-3xl font-black text-[#80C753] tabular-nums">{fmtUSD(calc.ingresoCertMes)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">100% se queda el broker</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Ingreso visas/mes</p>
                <p className="text-2xl sm:text-3xl font-black text-[#c9a227] tabular-nums">{fmtUSD(calc.ingresoVisaMes)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Exit sale del modelo Travel Empire</p>
              </div>
            </div>
            <div className="border-t border-white/10 pt-5">
              <p className="text-sm text-slate-300 leading-relaxed">
                <strong className="text-white">Total bruto: {fmtUSD(calc.ingresoTotalMes)}/mes</strong> menos <strong className="text-red-400">{fmtUSD(calc.costoLeadsMes)}</strong> de marketing
                = <strong className="text-[#c9a227]">{fmtUSD(calc.utilidadMes)}/mes limpios</strong> (
                {fmtUSD(calc.utilidadAnual)}/año). Este negocio funciona <strong className="text-white">en paralelo</strong> a la sala de ventas — los costos de oficina ya están cubiertos por la operación principal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cert-cta" className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 text-center">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#80C753] mb-3">Tu siguiente paso</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-4">
            ¿Quieres operar este negocio?
          </h2>
          <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            El Manual completo te enseña cómo armar tu operación de certificados, captar leads a $4 y operar con cierres del 5%+.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <Link
              to="/manual27"
              data-testid="cert-cta-manual27"
              className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#c9a227] to-[#e0b835] hover:from-[#e0b835] hover:to-[#c9a227] text-[#0a0a14] font-black text-base px-8 py-4 rounded-xl uppercase tracking-wider transition-all hover:scale-[1.03] shadow-lg shadow-[#c9a227]/30"
            >
              <Lock className="h-4 w-4" /> Acceder por $27 USD
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/analisisfinanciero"
              data-testid="cert-cta-volver"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-[#1D1D1B] font-bold text-sm px-6 py-4 rounded-xl uppercase tracking-wider transition-colors border border-slate-200"
            >
              <ArrowLeft className="h-4 w-4" /> Ver Análisis Financiero principal
            </Link>
          </div>

          <p className="text-xs text-slate-400">
            <span className="line-through">$297</span> · Hoy $27 USD · Acceso inmediato
          </p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-xs text-slate-400">
          Tons of Leads LLC · Florida Seller of Travel ST39568 · DBA Viajemos · Negocio Certificados — Edición 2026.
          <br />
          Cifras proyectadas. Resultados reales pueden variar.
        </div>
      </footer>
      <WhatsAppFAB context="certificados" />
    </div>
  );
};

export default CertificadosSimuladorPage;
